import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/seo/SEOHead';

const ClemensBrielsPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const schemaData = [
        {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "name": "Clemens Briels: Biografie, Antipodisme en Kunstwerken",
            "description": "Complete biografie van Clemens Briels (geboren 1946, Son, Nederland): grondlegger van het Antipodisme, officieel Olympisch Kunstenaar Salt Lake City 2002, internationaal tentoongesteld op vijf continenten. Originele werken beschikbaar via Kunstlounge.",
            "url": "https://www.kunstlounge.nl/clemens-briels/",
            "mainEntity": {
                "@type": "Person",
                "name": "Clemens Briels",
                "birthDate": "1946",
                "birthPlace": {
                    "@type": "Place",
                    "name": "Son",
                    "addressCountry": "NL",
                    "addressRegion": "Noord-Brabant"
                },
                "nationality": "Nederlands",
                "jobTitle": "Beeldend Kunstenaar",
                "knowsAbout": ["Antipodisme", "Zeefdruk", "Acrylschilderij", "Sculptuur", "Glaskunst", "Meubelontwerp"],
                "description": "Clemens Briels (geboren 1946 in Son, Noord-Brabant) is een Nederlandse beeldend kunstenaar en de grondlegger van het Antipodisme. Hij was officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City in 2002, als eerste Nederlandse kunstenaar ooit geselecteerd voor een Olympische posterreeks. Zijn werk hangt in de collecties van KLM, Philips, ABN AMRO, Ford en Holland Casino, en werd tentoongesteld in onder meer Miami, New York, Stockholm, Singapore en Shanghai.",
                "url": "https://www.kunstlounge.nl/clemens-briels/",
                "sameAs": [
                    "https://nl.wikipedia.org/wiki/Clemens_Briels",
                    "https://www.clemensbriels.nl",
                    "https://www.artsy.net/artist/clemens-briels",
                    "https://www.artnet.com/artists/clemens-briels/"
                ],
                "hasOccupation": {
                    "@type": "Occupation",
                    "name": "Beeldend Kunstenaar"
                },
                "award": "Officieel Olympisch Kunstenaar, Winterspelen Salt Lake City 2002",
                "affiliation": [
                    { "@type": "Organization", "name": "KLM Royal Dutch Airlines" },
                    { "@type": "Organization", "name": "Philips" },
                    { "@type": "Organization", "name": "ABN AMRO" },
                    { "@type": "Organization", "name": "Ford Motor Company" }
                ]
            },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kunstlounge.nl/" },
                    { "@type": "ListItem", "position": 2, "name": "Clemens Briels", "item": "https://www.kunstlounge.nl/clemens-briels/" }
                ]
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "Is Clemens Briels nog actief als kunstenaar?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Ja. Clemens Briels is actief. In juni 2024 exposeerde hij nog bij Zanolino Art Gallery met de tentoonstelling \"Images from Unknown Sources\". Zijn werk is actueel aanwezig op internationale kunstplatforms zoals Artsy, Artnet, 1stDibs en Art Brokerage."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Wat zijn bekende werktitels van Clemens Briels?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Bekende titels zijn onder meer: \"A Jump for Joy\" (2002, Olympisch werk op snowboard), \"The Fragrance of Park City\" (2002), \"You Carved Out Your Name in My Heart, I Was Touched to the Core\" (2004), \"Algunos Gentes Hacen la Comedia, Otros No\" (2005), \"Antigua, a Bay a Day\" (2000), \"Yellow Beard of Vegas\", \"Me Empresta Seus Olhos\" en \"El Dialogo\"."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Wat is de waardeontwikkeling van werk van Clemens Briels?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Clemens Briels' werken worden internationaal verhandeld op de secundaire markt. Gelimiteerde zeefdrukken waarvan de originele edities volledig zijn uitverkocht bij alle galeries zijn uitsluitend via de secundaire markt te verkrijgen. Zijn Olympische status, internationale tentoonstellingsgeschiedenis en aanwezigheid in grote bedrijfscollecties geven zijn werk een stabiele marktpositie. Actuele prijzen zijn te volgen via Invaluable.com en Artnet."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Heeft Clemens Briels een Wikipedia-pagina?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Ja. Clemens Briels heeft een Nederlandstalige Wikipedia-pagina op nl.wikipedia.org/wiki/Clemens_Briels. Zijn werk is bovendien gedocumenteerd op Wikidata, Artsy, Artnet en andere gezaghebbende kunstdatabanken."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Wie is Clemens Briels?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Clemens Briels (geboren 1946 in Son, Noord-Brabant) is een Nederlandse beeldend kunstenaar en de grondlegger van het Antipodisme. Hij was officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City in 2002. Zijn werk hangt in internationale collecties van KLM, Philips, ABN AMRO en Ford en werd tentoongesteld op vijf continenten."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Waar hangt werk van Clemens Briels?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Werk van Clemens Briels hangt in internationale collecties van KLM, Philips, ABN AMRO, Ford, Fortis, Pfizer, Sara Lee, DSM Chemicals en Holland Casino. Hij heeft tentoongesteld op vijf continenten en was officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City (2002)."
                    }
                }
            ]
        }
    ];

    const facts = [
        { label: 'Geboren', value: '1946, Son (Noord-Brabant)' },
        { label: 'Nationaliteit', value: 'Nederlands' },
        { label: 'Kunststijl', value: 'Antipodisme' },
        { label: 'Opleiding', value: 'Academie voor Industriële Vormgeving, Eindhoven (1965)' },
        { label: 'Olympisch Kunstenaar', value: 'Salt Lake City 2002' },
        { label: 'Media', value: 'Acryl, zeefdruk, sculptuur, glas, 3D-wandobject' },
        { label: 'Tentoongesteld', value: 'Vijf continenten' },
        { label: 'Museumexpositie', value: 'Shanghai, augustus 2008' },
    ];

    const corporateClients = [
        'KLM Royal Dutch Airlines',
        'Philips',
        'ABN AMRO',
        'Ford Motor Company',
        'Fortis',
        'Pfizer',
        'Sara Lee',
        'DSM Chemicals',
        'Holland Casino',
        'Leolux (meubelontwerp)',
    ];

    const exhibitionCities = [
        'Miami', 'New York', 'Stockholm', 'Antwerpen', 'Hannover', 'Stuttgart',
        'Kuala Lumpur', 'Singapore', 'Shanghai', 'Curaçao', 'Sint Maarten',
        'Midden-Oosten', 'Volksrepubliek China',
    ];

    return (
        <main id="main-content" className="pt-20">
            <SEOHead
                title="Clemens Briels: biografie en werken | Kunstlounge"
                description="Clemens Briels (1946): grondlegger Antipodisme, Olympisch Kunstenaar 2002. Werk bij KLM, Philips, ABN AMRO. Biografie, stijl en originelen beschikbaar via Kunstlounge."
                canonical="/clemens-briels/"
                imageUrl="/images/collectie/you-carved-out-your-name/you-carved-out-your-name-1.webp"
                schemaData={schemaData}
                hreflangEn="/en/clemens-briels.html"
                hreflangNl="/clemens-briels/"
            />

            {/* Hero */}
            <section className="relative py-32 bg-white overflow-hidden">
                <div className="container mx-auto px-6">
                    <span className="block text-accent text-xs tracking-[0.4em] uppercase mb-8">
                        Grondlegger van het Antipodisme
                    </span>
                    <h1 className="text-6xl md:text-8xl font-serif text-primary mb-10 leading-tight">
                        Clemens Briels<br />
                        <span className="text-gray-300 font-light italic">Geboren 1946, Son</span>
                    </h1>
                    <p className="text-xl text-gray-500 font-light max-w-2xl leading-relaxed">
                        Nederlandse beeldend kunstenaar, grondlegger van het Antipodisme, en de enige Nederlander die ooit officieel werd geselecteerd als Olympisch Kunstenaar, voor de Winterspelen van Salt Lake City in 2002.
                    </p>
                </div>
            </section>

            {/* Fact box */}
            <section className="bg-gray-50 py-12 border-t border-b border-gray-100">
                <div className="container mx-auto px-6">
                    <dl className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {facts.map(({ label, value }) => (
                            <div key={label}>
                                <dt className="text-[10px] uppercase tracking-[0.3em] text-accent font-bold mb-1">{label}</dt>
                                <dd className="text-sm text-primary font-serif leading-snug">{value}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </section>

            {/* Content */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-4xl">
                    <div className="prose prose-lg max-w-none font-light text-gray-600 leading-relaxed">

                        {/* Biografie */}
                        <h2 className="text-3xl font-serif text-primary mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Biografie: van reclame naar atelier
                        </h2>
                        <p className="mb-8">
                            <strong>Clemens Briels</strong> werd in 1946 geboren in Son, een gemeente in de Brabantse Kempen. Zijn artistieke aanleg manifesteerde zich vroeg: op elfjarige leeftijd won hij al een tekenwedstrijd op zijn basisschool. In 1965 begon hij zijn opleiding aan de Academie voor Industriële Vormgeving in Eindhoven, met als uitgesproken doel kunstenaar te worden. Op aandringen van zijn ouders sloeg hij aanvankelijk een andere weg in.
                        </p>
                        <p className="mb-8">
                            Briels bouwde een succesvolle carrière op in de reclame- en ontwerpwereld, achtereenvolgens als grafisch ontwerper, illustrator, art director en uiteindelijk creatief directeur. Het vak leverde hem scherpte op in compositie, kleurgebruik en het vertalen van een boodschap naar een beeld. Maar de drang om te schilderen liet hij op 46-jarige leeftijd definitief de overhand nemen. Rond 1992 keerde hij de advertentiewereld de rug toe en wijdde hij zich volledig aan de vrije beeldende kunst.
                        </p>
                        <p className="mb-12">
                            Wat volgde was een internationale carrière die hem naar galeries en musea op vijf continenten bracht, met erkenning van multinational tot olympisch comité.
                        </p>

                        {/* Antipodisme */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Het Antipodisme: een kunstfilosofie
                        </h2>
                        <p className="mb-8">
                            Briels noemt zijn stijl het <strong>Antipodisme</strong>, met als ondertitel <em>"Images from Unknown Sources"</em>. Hij omschrijft het als zijn persoonlijke vierde dimensie, waarin elk bestaan een direct en tegenovergesteld bestaan heeft. In zijn eigen woorden: <em>"The colour is the vehicle, the message is what it is all about."</em>
                        </p>
                        <p className="mb-8">
                            Het Antipodisme draait om contrasten die elkaar niet uitsluiten maar versterken: kleur tegenover stilte, humor tegenover ernst, het individuele tegenover de massa, beweging tegenover rust. Briels transformeert dagelijkse thema's opzettelijk tot iets onverwachts: herkenbaar genoeg om te raken, afwijkend genoeg om te onthouden. De sterke Latijns-Amerikaanse invloeden komen voort uit zijn reizen door de Cariben, Noord- en Zuid-Amerika en het Midden-Oosten.
                        </p>
                        <p className="mb-8">
                            Kenmerkend is ook zijn welbewuste minachting voor het academische perspectief. Waar tradities dwingen tot een vastomlijnd ruimtegevoel, creëert Briels zijn eigen visuele realiteit. Figuren zijn expressief vergroot, composities bewegen van links naar rechts als muziek, en de kleurpaletten bevatten altijd een onverwachte combinatie die geen enkele andere kunstenaar zou durven.
                        </p>

                        <div className="my-20 p-12 bg-gray-50 border-l-4 border-accent shadow-sm">
                            <p className="text-xl font-serif italic text-primary leading-relaxed">
                                "The colour is the vehicle, the message is what it is all about."
                            </p>
                            <p className="text-sm text-gray-400 mt-4 font-light">Clemens Briels</p>
                        </div>

                        {/* Olympisch Kunstenaar */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Olympisch Kunstenaar: Salt Lake City 2002
                        </h2>
                        <p className="mb-8">
                            In 2002 werd Clemens Briels de <strong>officiële kunstenaar van de Olympische Winterspelen in Salt Lake City</strong>. Het was de eerste keer in de geschiedenis dat een Nederlandse beeldend kunstenaar voor deze eretitel werd geselecteerd. Zijn Olympisch werk <em>"A Jump for Joy"</em> werd geschilderd op een snowboard en werd als gelimiteerde driedimensionale zeefdruk wereldwijd uitgebracht. Een tweede werk uit deze periode, <em>"The Fragrance of Park City"</em>, verscheen eveneens als zeefdruk.
                        </p>
                        <p className="mb-12">
                            De Olympische erkenning bevestigde wat galeries in Europa, Noord-Amerika en Azië al wisten: Briels' werk overstijgt culturele grenzen. Het combineert universele thema's van beweging, vrijheid en menselijke verbinding met een zo uitgesproken persoonlijke stijl dat het onmiddellijk herkenbaar is.
                        </p>

                        {/* Internationale Exposities */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Internationaal tentoongesteld op vijf continenten
                        </h2>
                        <p className="mb-6">
                            In de loop van zijn carrière exposeerde Clemens Briels in steden en regio's op vijf continenten. Een selectie:
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                            {exhibitionCities.map(city => (
                                <div key={city} className="flex items-center gap-2 text-gray-600 font-light text-sm">
                                    <span className="text-accent flex-shrink-0">&#x2022;</span>
                                    <span>{city}</span>
                                </div>
                            ))}
                        </div>
                        <p className="mb-12">
                            In augustus 2008 vond zijn museumtentoonstelling in Shanghai plaats. In juni 2024 exposeerde hij bij Zanolino Art Gallery met de tentoonstelling <em>"Images from Unknown Sources"</em>, de titel die zijn Antipodisme-filosofie het scherpst samenvat.
                        </p>

                        {/* Corporatieve Opdrachten */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Corporatieve opdrachten en samenwerkingen
                        </h2>
                        <p className="mb-6">
                            Naast zijn vrije werk ontving Briels opdrachten van een groot aantal internationale bedrijven. Zijn werk bevindt zich in de collecties of het productontwerp van:
                        </p>
                        <ul className="space-y-2 mb-8 list-none pl-0">
                            {corporateClients.map(client => (
                                <li key={client} className="flex items-center gap-3 text-gray-600 font-light text-sm border-b border-gray-50 pb-2">
                                    <span className="text-accent flex-shrink-0">&#x2022;</span>
                                    <span>{client}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mb-8">
                            Twee samenwerkingen verdienen bijzondere vermelding. In 1995 ontwierp Briels samen met meubelontwerper Frans Schrofer de <strong>Amphora-fauteuil</strong> voor Leolux, voorzien van een speciaal geabstraheerd fluweel in zijn kenmerkende kleuren. En voor Ford ontwierp hij de bekleding voor een bijzondere editie van de Ford Ka: vijftig exemplaren kregen zijn stofontwerp, gevolgd door duizend extra exemplaren in de <em>"Art with a Large Ka"</em>-editie met zijn signatuur.
                        </p>
                        <p className="mb-12">
                            In de Tsjechische Republiek ontwierp Briels mondgeblazen vazen, die handmatig werden vervaardigd door glasblazers in de traditionele Boheemse glastraditie.
                        </p>

                        {/* Publicaties */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Publicaties
                        </h2>
                        <ul className="space-y-6 mb-12 list-none pl-0">
                            <li className="flex gap-4 items-start border-b border-gray-100 pb-6">
                                <span className="text-accent mt-1 flex-shrink-0 text-base">&#x2022;</span>
                                <span>
                                    <strong className="font-serif text-primary block mb-1">Clemens Briels: 1998-2002 (monografie)</strong>
                                    <span className="font-light text-sm">Auteur: Tatie Daal. Uitgever: Press/Art Editions, Eindhoven (2002). Bevat schilderijen, objecten, sculpturen, meubelontwerpen, zeefdrukken en corporatieve opdrachten uit de periode 1998-2002.</span>
                                </span>
                            </li>
                            <li className="flex gap-4 items-start border-b border-gray-100 pb-6">
                                <span className="text-accent mt-1 flex-shrink-0 text-base">&#x2022;</span>
                                <span>
                                    <strong className="font-serif text-primary block mb-1">Antipodism (gelimiteerde editie)</strong>
                                    <span className="font-light text-sm">Genummerde en door de kunstenaar gesigneerde editie van 750 exemplaren. Inleiding door Hennie van de Louw. Grondig overzicht van de Antipodisme-filosofie en het oeuvre van Briels.</span>
                                </span>
                            </li>
                        </ul>

                        {/* Media en technieken */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">
                            Technieken en media
                        </h2>
                        <p className="mb-8">
                            Clemens Briels werkt bewust in meerdere media en beschouwt de verscheidenheid als onderdeel van zijn kunstpraktijk. Zijn vakgebieden:
                        </p>
                        <ul className="space-y-3 mb-12 list-none pl-0">
                            {[
                                { t: 'Acryl op doek', v: 'Originele unieke schilderijen in uitgesproken kleuropposities' },
                                { t: 'Zeefdruk (serigraphie)', v: 'Gelimiteerde edities, soms in 3D-uitvoering geschilderd op ongebruikelijke dragers zoals snowboards' },
                                { t: 'Sculptuur', v: 'Driedimensionale figuren en wandobjecten in acryl op hars' },
                                { t: 'Glaskunst', v: 'Mondgeblazen vazen vervaardigd in de Tsjechische Republiek' },
                                { t: 'Meubelontwerp', v: 'Stoffen, tapijten en meubels, o.a. voor Leolux' },
                                { t: 'Productontwerp', v: 'Bekleding en stofontwerp voor de automobielindustrie (Ford)' },
                            ].map(({ t, v }) => (
                                <li key={t} className="flex gap-4 items-start border-b border-gray-100 pb-4">
                                    <span className="text-accent mt-1 flex-shrink-0 text-base">&#x2022;</span>
                                    <span>
                                        <strong className="font-serif text-primary">{t}: </strong>
                                        <span className="font-light">{v}</span>
                                    </span>
                                </li>
                            ))}
                        </ul>

                    </div>

                    {/* Werken in de collectie */}
                    <div className="mt-24 pt-20 border-t border-gray-100">
                        <h2 className="text-3xl font-serif text-primary mb-4">Werken van Clemens Briels in de Kunstlounge collectie</h2>
                        <p className="text-gray-500 font-light mb-16 max-w-2xl">
                            Kunstlounge.nl biedt originele werken direct aan vanuit een privécollectie. Elk werk is voorzien van een door Clemens Briels persoonlijk gesigneerd certificaat van echtheid.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
                            {[
                                {
                                    slug: 'you-carved-out-your-name',
                                    title: 'You Carved Out Your Name',
                                    subtitle: 'Origineel schilderij, 1/1, 130x100 cm, 2004',
                                    img: '/images/collectie/you-carved-out-your-name/you-carved-out-your-name.jpg',
                                    alt: 'You carved out your name in my heart, origineel schilderij van Clemens Briels'
                                },
                                {
                                    slug: 'algunos-gentes-hacen-la-comedia',
                                    title: 'Algunos Gentes Hacen la Comedia',
                                    subtitle: 'Kleurenzeefdruk 97/175, 104x155 cm, 2005',
                                    img: '/images/collectie/algunos-gentes-hacen-la-comedia/algunos-gentes-hacen-la-comedia-otros-no-1.webp',
                                    alt: 'Algunos Gentes Hacen la Comedia, zeefdruk van Clemens Briels'
                                },
                                {
                                    slug: 'antigua-a-bay-a-day',
                                    title: 'Antigua, a Bay a Day',
                                    subtitle: 'Kleurenzeefdruk 132/175, 95x110 cm, 2000',
                                    img: '/images/collectie/antigua-a-bay-a-day/antigua-a-bay-a-day-1.webp',
                                    alt: 'Antigua a Bay a Day, zeefdruk van Clemens Briels'
                                },
                            ].map(({ slug, title, subtitle, img, alt }) => (
                                <Link key={slug} to={`/artwork/${slug}`} className="group block">
                                    <div className="overflow-hidden bg-gray-50 mb-4 shadow-sm group-hover:shadow-md transition-shadow duration-500">
                                        <img
                                            src={img}
                                            alt={alt}
                                            className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                                            style={{ maxHeight: '260px', objectFit: 'contain' }}
                                        />
                                    </div>
                                    <h3 className="font-serif text-primary text-lg mb-1">{title}</h3>
                                    <p className="text-gray-400 text-xs font-light tracking-wide">{subtitle}</p>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* FAQ */}
                    <div className="mt-4 pt-20 border-t border-gray-100">
                        <h2 className="text-3xl font-serif text-primary mb-12">Veelgestelde vragen over Clemens Briels</h2>
                        <div className="space-y-8">
                            {[
                                {
                                    q: 'Is Clemens Briels nog actief als kunstenaar?',
                                    a: 'Ja. Clemens Briels is actief. In juni 2024 exposeerde hij nog bij Zanolino Art Gallery met de tentoonstelling "Images from Unknown Sources". Zijn werk is actueel aanwezig op internationale kunstplatforms zoals Artsy, Artnet, 1stDibs en Art Brokerage.'
                                },
                                {
                                    q: 'Wat zijn bekende werktitels van Clemens Briels?',
                                    a: 'Bekende titels zijn onder meer: "A Jump for Joy" (2002, Olympisch werk op snowboard), "The Fragrance of Park City" (2002), "You Carved Out Your Name in My Heart, I Was Touched to the Core" (2004), "Algunos Gentes Hacen la Comedia, Otros No" (2005), "Antigua, a Bay a Day" (2000), "Yellow Beard of Vegas", "Me Empresta Seus Olhos" en "El Dialogo".'
                                },
                                {
                                    q: 'Wat is de waardeontwikkeling van werk van Clemens Briels?',
                                    a: 'Clemens Briels\' werken worden internationaal verhandeld op de secundaire markt. Gelimiteerde zeefdrukken waarvan de originele edities volledig zijn uitverkocht bij alle galeries zijn uitsluitend via de secundaire markt te verkrijgen. Zijn Olympische status, internationale tentoonstellingsgeschiedenis en aanwezigheid in grote bedrijfscollecties geven zijn werk een stabiele marktpositie. Actuele prijzen zijn te volgen via Invaluable.com en Artnet.'
                                },
                                {
                                    q: 'Heeft Clemens Briels een Wikipedia-pagina?',
                                    a: 'Ja. Clemens Briels heeft een Nederlandstalige Wikipedia-pagina op nl.wikipedia.org/wiki/Clemens_Briels. Zijn werk is bovendien gedocumenteerd op Wikidata, Artsy, Artnet en andere gezaghebbende kunstdatabanken.'
                                },
                                {
                                    q: 'Wie is Clemens Briels?',
                                    a: 'Clemens Briels (geboren 1946 in Son, Noord-Brabant) is een Nederlandse beeldend kunstenaar en de grondlegger van het Antipodisme. Hij was officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City in 2002. Zijn werk hangt in internationale collecties van KLM, Philips, ABN AMRO en Ford en werd tentoongesteld op vijf continenten.'
                                },
                                {
                                    q: 'Waar hangt werk van Clemens Briels?',
                                    a: 'Werk van Clemens Briels hangt in internationale collecties van KLM, Philips, ABN AMRO, Ford, Fortis, Pfizer, Sara Lee, DSM Chemicals en Holland Casino. Hij heeft tentoongesteld op vijf continenten en was officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City (2002).'
                                },
                            ].map(({ q, a }) => (
                                <div key={q} className="border-b border-gray-100 pb-8">
                                    <h3 className="font-serif text-primary text-xl mb-3">{q}</h3>
                                    <p className="text-gray-500 font-light leading-relaxed">{a}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="mt-20 flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <Link
                            to="/#collectie"
                            className="inline-block px-12 py-5 border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold"
                        >
                            Bekijk de Collectie
                        </Link>
                        <Link
                            to="/antipodisme"
                            className="inline-block px-12 py-5 border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold"
                        >
                            Het Antipodisme
                        </Link>
                        <Link
                            to="/aanvraag"
                            className="inline-block px-12 py-5 bg-primary text-white hover:bg-accent transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold shadow-xl"
                        >
                            Informatie Aanvragen
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default ClemensBrielsPage;
